import { useIsShortlistingPermitted } from "PFCore/helpers/shortlists";
import { useActivityParticipants } from "PFCore/hooks/queries/activity/use_activity_participants";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Activity } from "PFTypes";
import { useMemo } from "react";

import { useTemplate } from "../../../hooks";

const checkRole = (key: string) => ["role", "audit_role"].includes(key);
const checkEngagement = (key: string) => ["engagement", "audit_engagement"].includes(key);
const isAuditKey = (key: string | undefined) => !!key?.includes("audit_");

export const useActivityPermissions = (activity: Activity) => {
  const { data: currentProfile } = useCurrentProfile();
  const { data: participants } = useActivityParticipants(activity.id);

  const template = useTemplate(activity.template_id);
  const needMatches = template?.need_matches;

  const isRole = checkRole(template?.key || "");
  const isEngagement = checkEngagement(template?.key || "");
  const isAudit = isAuditKey(template?.key);

  const ownGroup = currentProfile.permissions_group;

  const isResourcer = !!ownGroup?.permissions.find(
    ({ name, permitted }) => name === "resourcer" && permitted
  );
  const isReviewer = !!activity.reviewers?.find(({ profile_id }) => profile_id === currentProfile.id);

  const isShortlistingPermitted = useIsShortlistingPermitted()(activity);

  // todo: [PROF-6072] allowing to see matches in the audit planner
  //  without being resourcer or audit_resourcer is most likely an invalid behaviour
  //  check: https://profinda.slack.com/archives/C02J0HP1ZRP/p1713272096105739?thread_ts=1712749315.559599&cid=C02J0HP1ZRP for details
  const canSeeMatches = useMemo(
    () => !!(needMatches && activity.coowned && (!isRole || isResourcer || isAudit)),
    [activity.coowned, isAudit, isResourcer, isRole, needMatches]
  );

  const canSeeShortlist = useMemo(
    () => !!(((needMatches && isShortlistingPermitted) || isReviewer) && isRole),
    [isReviewer, isRole, isShortlistingPermitted, needMatches]
  );

  const canAddShortlist = isResourcer && isShortlistingPermitted;
  const canReviewShortlists = activity.coowned || isReviewer;

  const canSeeVacancies = activity.coowned && isRole;
  const canSeeRoles = activity.coowned && isEngagement;
  const canSeeInterested = activity.coowned && isRole;
  const canSeeFeedback = !!(activity.coowned && isEngagement && participants && participants?.meta.total > 0);

  return {
    canSeeMatches,
    canSeeShortlist,
    canSeeVacancies,
    canSeeRoles,
    canSeeInterested,
    canSeeFeedback,
    canAddShortlist,
    canReviewShortlists
  };
};
